import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import React, { useContext } from 'react';

import CloseButton from 'presentation/containers/CloseButtonContainer';
import { getCustomText } from 'app/modules';
import { getOngoingChat } from 'app/modules';
import { isAlternateBucket } from 'lib/handleBucketLogic';
import IconContainer from 'presentation/components/icons/IconContainer';
import uiEvents from 'events/ui';
import overrideStyle from 'lib/overrideStyle';
import { useThrottled } from 'presentation/hooks/debounce';

export function Header(props) {
  const onClick = useThrottled(props.onNavigateBack, 1000);
  const onKeyDown = ev => {
    if (ev.key === 'Enter') {
      onClick();
    }
  };
  const { isMobileSdk } = useContext(ThemeContext);

  if (isMobileSdk) {
    return null;
  }

  const renderBackButton = () => {
    if (!props.isABTestingOnboarding) {
      if (props.shouldDisplayBackButton) {
        return (
          <BackButton
            aria-labelledby="back"
            data-aid="selfService-header-backButton"
            role="button"
            onClick={onClick}
            onKeyDown={onKeyDown}
            tabIndex="0"
          >
            <InnerWrapper tabIndex="-1">
              <Chevron />
              <BackButtonText>{props.backButtonText}</BackButtonText>
            </InnerWrapper>
          </BackButton>
        );
      }
    }
  };

  const renderTitle = () => {
    if (!props.shouldDisplayTitle) {
      return null;
    }
    return props.isChatting ? (
      <Greeting>{props.greeting}</Greeting>
    ) : (
      <Title aria-label="header">
        <span>{props.headerTitle}</span>
      </Title>
    );
  };

  return (
    <HeaderWrapper isChatting={props.isChatting} data-aid="selfService-header">
      {props.isChatting || props.shouldDisplayBackButton ? (
        <LeftHeaderBoxWrapper>{renderBackButton()}</LeftHeaderBoxWrapper>
      ) : null}

      <CenterHeaderBoxWrapper aria-live="polite">{renderTitle()}</CenterHeaderBoxWrapper>

      <RightHeaderBoxWrapper>
        <CloseButton setAutoFocusMinimizedChat={props.setAutoFocusMinimizedChat} />
      </RightHeaderBoxWrapper>
    </HeaderWrapper>
  );
}

Header.propTypes = {
  isABTestingOnboarding: PropTypes.bool,
  isChatting: PropTypes.bool,
  onNavigateBack: PropTypes.func,
  setAutoFocusMinimizedChat: PropTypes.func.isRequired,
  shouldDisplayBackButton: PropTypes.bool,
  shouldDisplayTitle: PropTypes.bool,
  title: PropTypes.string,
};

const BackButton = overrideStyle('div')`
  & svg {
    cursor: pointer;
    height: 13px;
    margin-right: 5px;
    width: 8px;
  }

  &:focus {
    box-shadow: inset 0 0 0px 2px #81b5ea;
    outline: solid 1px #81b5ea;
  }
`;

const InnerWrapper = overrideStyle('div')`
  align-items: center;
  box-shadow: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  outline: none;
  width: auto;
`;

const BackButtonText = overrideStyle('div')`
  cursor: pointer;
  font-family: ${p => p.theme.fontLibrary.primary};
  font-size: 13px;
`;

const Greeting = overrideStyle('div')`
  font-family: ${p => p.theme.fontLibrary.primary};
  font-size: 13px;
  font-weight: bold;
  flex: 1 auto;
`;

const HeaderWrapper = overrideStyle('div')`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  font-family: ${p => p.theme.fontLibrary.primary};
  height: 16px;
  justify-content: space-between;
  padding: 24px;
  padding-left: ${p => (p.isChatting ? '18px' : '24px')};
  padding-bottom: ${p => p.isChatting && '18px'};
`;

const Title = overrideStyle('div')`
  font-family: ${p => p.theme.fontLibrary.primary};
  font-size: 18px;
  font-weight: 500;
  width: 100%;
`;

// Secondary Flexboxes
const CenterHeaderBoxWrapper = overrideStyle('div')`
  display: flex;
  justify-content: center;
`;

const LeftHeaderBoxWrapper = overrideStyle('div')`
  display: flex;
  justify-content: flex-start;
  width: 50px;
`;

const RightHeaderBoxWrapper = overrideStyle('div')`
  display: flex;
  justify-content: flex-end;
  width: 50px;
`;

export const mapStateToProps = (state, props) => {
  let customText = getCustomText(state);
  return {
    ...props,
    backButtonText: customText.header.backButton,
    headerTitle: customText.header.title || customText.headerTitle,
    isABTestingOnboarding: isAlternateBucket(state.configuration.onboardingTypeTestBucket),
    greeting: customText.header.greeting || getOngoingChat(state).title,
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    onNavigateBack: () => dispatch(uiEvents.navigateBack()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Header);

function Chevron() {
  return (
    <IconContainer viewBox="0 0 256 512">
      <path
        fill="currentColor"
        d="M238.475 475.535l7.071-7.07c4.686-4.686 4.686-12.284 0-16.971L50.053 256 245.546 60.506c4.686-4.686 4.686-12.284 0-16.971l-7.071-7.07c-4.686-4.686-12.284-4.686-16.97 0L10.454 247.515c-4.686 4.686-4.686 12.284 0 16.971l211.051 211.05c4.686 4.686 12.284 4.686 16.97-.001z"
      />
    </IconContainer>
  );
}
